import { useEffect, useState } from "react";
import axios from "axios";
import type { UpsellPageDTO } from "../types/upsell.types";

const upsellApiUrl = (postId: number | string): string => {
  const apiUrl = window.apiUrl ?? "/wp-json/";
  const baseUrl = apiUrl.endsWith("/") ? apiUrl : `${apiUrl}/`;

  return `${baseUrl}api/v2/upsell-page-dto/${postId}`;
};

export const useUpsellApi = () => {
  const [data, setData] = useState<UpsellPageDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const postId = window.postId;

    if (!postId) {
      setError(new Error("Missing WordPress postId"));
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get<UpsellPageDTO>(upsellApiUrl(postId), {
          headers: {
            "X-CC-CSRF-Token": window.ccRestAccess?.csrfToken ?? "",
          },
          withCredentials: true,
        });

        if (isMounted) {
          setData(response.data);
        }
      } catch (err) {
        if (isMounted) {
          setError(
            axios.isAxiosError(err)
              ? new Error(err.response?.data?.message || err.message)
              : new Error("Failed to fetch upsell page data"),
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return { data, isLoading, error };
};
